import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { DetailTableLayout } from '@/components/common/DetailTableLayout';
import { useLocalListSearch } from '@/hooks/useLocalListSearch';
import { useRememberViewedRow, useRevealLastViewedRow } from '@/hooks/useLastViewedRow';
import { getZmHandOutStatCards } from '@/utils/handOutStatCards';
import {
  distributedSpecimenQueryOptions,
  distributionMembersQueryOptions,
} from '../api/product.api';
import { getDistributionMemberColumns } from '../components/ProductColumns';
import { NO_SEARCH_MATCH_MESSAGE, SPECIMEN_DRILL_ORIGIN } from '../constants/product.constants';
import type { DistributionMember } from '../types/product.types';
import { toDistributionMemberParams, toSpecimenDrillSearch } from '../utils/productFilters';

const route = getRouteApi('/_auth/product/specimen-distribution/$specimenId/');

const UNKNOWN_SPECIMEN_TITLE = 'Specimen';

const toSearchableZmValues = (zm: DistributionMember) => [zm.name, zm.hrEmpId];

interface SpecimenZmsPageProps {
  specimenId: number;
}

export function SpecimenZmsPage({ specimenId }: SpecimenZmsPageProps) {
  const routeSearch = route.useSearch();
  const navigate = route.useNavigate();
  const appliedSearchText = routeSearch.q;
  const zmSearch = useMemo(() => toSpecimenDrillSearch(routeSearch), [routeSearch]);
  const memberParams = toDistributionMemberParams(zmSearch);

  useRememberViewedRow('specimens', specimenId);
  const lastViewedZmId = useRevealLastViewedRow('specimenZms', specimenId);

  const specimenQuery = useQuery(distributedSpecimenQueryOptions(specimenId, memberParams));
  const zmsQuery = useQuery(distributionMembersQueryOptions(specimenId, memberParams));

  const { searchText, setSearchText, visibleRows } = useLocalListSearch({
    rows: zmsQuery.data,
    appliedText: appliedSearchText,
    onApply: (text) =>
      void navigate({ search: (previous) => ({ ...previous, q: text }), replace: true }),
    toSearchableValues: toSearchableZmValues,
  });

  const columns = useMemo(
    () => getDistributionMemberColumns(specimenId, zmSearch, appliedSearchText),
    [specimenId, zmSearch, appliedSearchText],
  );

  const title = specimenQuery.isLoading
    ? undefined
    : (specimenQuery.data?.name ?? UNKNOWN_SPECIMEN_TITLE);

  return (
    <DetailTableLayout
      title={title}
      titleAddon={<DateRangeBadge dateFrom={zmSearch.dateFrom} dateTo={zmSearch.dateTo} />}
      search={{ value: searchText, onChange: setSearchText, placeholder: 'ZM name or HR ID' }}
      onNoHistory={() =>
        void navigate({
          to: zmSearch.from ?? SPECIMEN_DRILL_ORIGIN.distribution,
          search: memberParams,
        })
      }
      stats={getZmHandOutStatCards(zmsQuery.data)}
      table={{
        rows: visibleRows,
        columns,
        isLoading: zmsQuery.isLoading,
        error: zmsQuery.error,
        errorTitle: 'Could not load the ZMs',
        onRetry: () => void zmsQuery.refetch(),
        stickyColumnId: 'name',
        emptyMessage: appliedSearchText
          ? NO_SEARCH_MATCH_MESSAGE
          : 'Nobody handed this specimen out in the period',
        isRowHighlighted: (zm) => zm.userId === lastViewedZmId,
      }}
    />
  );
}
